import React, { useId, useState } from "react";
import Dialog from "@mui/material/Dialog";
import { FiCheck, FiFileText, FiInfo, FiMic, FiX } from "react-icons/fi";
import AiBetaBadge from "../../common/AiBetaBadge";
import styles from "./RecordSessionHelp.module.scss";

function ConsultationAnimation() {
  return (
    <svg className={styles.illustration} viewBox="0 0 600 260" fill="none" role="img" aria-label="A doctor and patient talk while a microphone records their consultation and AI prepares a draft patient note.">
      <ellipse cx="282" cy="235" rx="219" ry="12" fill="#E7DDF7" />
      <rect x="110" y="28" width="340" height="197" rx="98" fill="#EDE4FB" />

      <path d="M102 166v50h72m-59 0v18m48-18v18" stroke="#AFA2CB" strokeWidth="8" strokeLinecap="round" />
      <path d="m153 190 6 39m26-39 13 39" stroke="#394A6B" strokeWidth="15" strokeLinecap="round" />
      <path d="M154 232h16m23 0h15" stroke="#2D3B55" strokeWidth="6" strokeLinecap="round" />
      <path d="M120 190v-45c0-19 16-30 35-30s34 11 34 30v45" fill="#FFF" stroke="#D9D5E5" strokeWidth="2" />
      <path d="m143 118 13 22 13-22" fill="#B5C6E8" />
      <rect x="146" y="101" width="20" height="23" rx="8" fill="#E8AB84" />
      <g className={styles.doctorHead}>
        <rect x="134" y="59" width="45" height="53" rx="21" fill="#F3C29E" />
        <path d="M133 83c-7-23 9-37 25-35 23 0 28 20 21 34l-7-15c-12 9-24 7-33 4l-6 12Z" fill="#34435E" />
        <circle cx="162" cy="83" r="2" fill="#34435E" />
        <path d="m171 85 4 5-4 2" stroke="#D79A75" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        <ellipse className={styles.doctorMouth} cx="165" cy="98" rx="3.5" ry="1" fill="#B8755B" />
      </g>
      <path d="M138 126v24c0 15 26 15 26 0v-24" stroke="#6685A3" strokeWidth="3" />
      <circle cx="164" cy="150" r="5" fill="#6685A3" />
      <path className={styles.doctorHand} d="m182 141 18 22 22-13" stroke="#F3C29E" strokeWidth="11" strokeLinecap="round" strokeLinejoin="round" />

      <path d="M411 166v50h-66m52 0v18m-43-18v18" stroke="#AFA2CB" strokeWidth="8" strokeLinecap="round" />
      <path d="m338 188-13 41m38-41-5 41" stroke="#555071" strokeWidth="15" strokeLinecap="round" />
      <path d="M314 232h17m16 0h16" stroke="#403B58" strokeWidth="6" strokeLinecap="round" />
      <path d="M321 190v-45c0-19 14-30 33-30 20 0 35 11 35 30v45" fill="#A98AD9" />
      <rect x="343" y="101" width="20" height="23" rx="8" fill="#CA8D68" />
      <g className={styles.patientHead}>
        <path d="M330 82c0-22 10-35 27-35 18 0 27 15 27 35v38h-53l-1-38Z" fill="#594056" />
        <rect x="332" y="61" width="43" height="51" rx="20" fill="#E6AD86" />
        <path d="M331 79c0-18 9-28 24-28 15 0 23 12 23 27l-12-15c-9 11-22 13-35 16Z" fill="#594056" />
        <circle cx="342" cy="83" r="2" fill="#594056" />
        <path d="m337 85-4 5 4 2" stroke="#C88D69" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        <ellipse className={styles.patientMouth} cx="341" cy="98" rx="3.5" ry="1" fill="#A4634A" />
      </g>
      <path className={styles.patientHand} d="m328 142-19 21-20-11" stroke="#E6AD86" strokeWidth="11" strokeLinecap="round" strokeLinejoin="round" />

      <g className={styles.doctorSpeech}>
        <path d="M79 17h94a10 10 0 0 1 10 10v13a10 10 0 0 1-10 10h-12l-9 10V50H79a10 10 0 0 1-10-10V27a10 10 0 0 1 10-10Z" fill="#FFF" stroke="#D4C1EF" />
        <path d="M83 29h72m-72 10h48" stroke="#B598DB" strokeWidth="3" strokeLinecap="round" />
      </g>
      <g className={styles.patientSpeech}>
        <path d="M341 17h94a10 10 0 0 1 10 10v13a10 10 0 0 1-10 10h-62l-9 10V50h-23a10 10 0 0 1-10-10V27a10 10 0 0 1 10-10Z" fill="#FFF" stroke="#D4C1EF" />
        <path d="M345 29h72m-72 10h48" stroke="#B598DB" strokeWidth="3" strokeLinecap="round" />
      </g>

      <rect x="183" y="177" width="130" height="9" rx="4.5" fill="#CBB9E3" />
      <path d="M197 187v46m102-46v46" stroke="#A994C3" strokeWidth="6" strokeLinecap="round" />
      <g className={styles.recordingActivity}>
        <circle className={styles.micHalo} cx="250" cy="147" r="26" stroke="#A984D9" strokeWidth="2" />
      </g>
      <rect x="244" y="130" width="12" height="23" rx="6" fill="#7C3AED" />
      <path d="M239 145v3a11 11 0 0 0 22 0v-3m-11 14v12m-8 0h16" stroke="#7C3AED" strokeWidth="3" strokeLinecap="round" />
      <g transform="translate(200 202)">
        <rect width="100" height="26" rx="13" fill="#FFF" stroke="#DDD0EF" />
        <circle cx="14" cy="13" r="4" fill="#D6C9E6" />
        <g className={styles.recordingActivity}>
          <circle className={styles.recordingDot} cx="14" cy="13" r="4" fill="#E35A77" />
          {[8, 14, 10, 18, 12, 7, 15, 10, 16, 8].map((height, index) => (
            <rect key={index} className={styles.waveBar} x={27 + index * 6} y={13 - height / 2} width="3" height={height} rx="1.5" fill="#9865D2" style={{ animationDelay: `${index * 90}ms` }} />
          ))}
        </g>
      </g>

      <g className={styles.processingActivity}>
        <path className={styles.transfer} d="M408 152h34m-7-6 7 6-7 6" stroke="#B59AD8" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" strokeDasharray="4 5" />
      </g>
      <g className={styles.note}>
        <rect x="452" y="100" width="111" height="119" rx="12" fill="#FFF" stroke="#D8C9EC" strokeWidth="2" />
        <rect x="466" y="115" width="27" height="27" rx="7" fill="#F0E6FC" />
        <path d="M474 121h8l4 4v11h-12v-15Zm8 0v5h4m-9 3h6m-6 3h6" stroke="#9865D2" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        <text x="466" y="161" fill="#68507F" fontSize="10" fontWeight="600">Draft patient note</text>
        {[174, 185, 196].map((y, index) => (
          <path key={y} className={styles.noteLine} d={`M467 ${y}h${index === 2 ? 49 : 78}`} stroke="#DACCEB" strokeWidth="4" strokeLinecap="round" style={{ animationDelay: `${index * 150}ms` }} />
        ))}
        <g className={styles.reviewReady}>
          <circle cx="550" cy="109" r="11" fill="#E6F5EC" stroke="#B6DFC5" />
          <path d="m545 109 3 3 6-6" stroke="#4D9670" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </g>
      </g>
      <text x="155" y="254" textAnchor="middle" fill="#78628F" fontSize="11" fontWeight="600">Doctor</text>
      <text x="354" y="254" textAnchor="middle" fill="#78628F" fontSize="11" fontWeight="600">Patient</text>
    </svg>
  );
}

export default function RecordSessionHelp() {
  const id = useId();
  const [open, setOpen] = useState(false);
  const close = () => setOpen(false);

  return (
    <>
      <button type="button" className={styles.infoButton} aria-label="How Record session works" aria-haspopup="dialog" title="How Record session works" onClick={() => setOpen(true)}>
        <FiInfo aria-hidden="true" />
      </button>
      <Dialog open={open} onClose={close} aria-labelledby={`${id}-title`} maxWidth={false} PaperProps={{ className: styles.paper }} className={styles.dialog}>
        <div className={styles.header}>
          <div>
            <p className={styles.eyebrow}>HOW IT WORKS <AiBetaBadge /></p>
            <h2 id={`${id}-title`}>What is Record session?</h2>
          </div>
          <button type="button" className={styles.closeButton} onClick={close} aria-label="Close Record session information"><FiX aria-hidden="true" /></button>
        </div>

        <div className={styles.demo}>
          <div className={styles.demoHeading}><span className={styles.demoLabel}><FiMic aria-hidden="true" /> A consultation, illustrated</span><span>Demo only</span></div>
          <div className={styles.animation}>
            <ConsultationAnimation />
            <div className={styles.phaseCaption} aria-hidden="true">
              <span className={styles.recordingCaption}>Recording the consultation</span>
              <span className={styles.processingCaption}>Transcribing the recording</span>
              <span className={styles.reviewCaption}>Draft ready for review</span>
            </div>
            <div className={styles.progress}><span /></div>
          </div>
          <p className={styles.demoNote}>This animation does not record audio.</p>
        </div>

        <ol className={styles.steps}>
          <li><span className={styles.stepIcon}><FiMic aria-hidden="true" /></span><div><h3>1. Record the conversation</h3><p>Open Record session and start recording while you and your patient discuss the consultation.</p></div></li>
          <li><span className={styles.stepIcon}><FiFileText aria-hidden="true" /></span><div><h3>2. Generate a draft</h3><p>Upload the recording when you finish. AI transcribes the conversation and prepares a draft patient note.</p></div></li>
          <li><span className={styles.stepIcon}><FiCheck aria-hidden="true" /></span><div><h3>3. Review and save</h3><p>Check and edit the draft, then confirm and save it to your consent bundle before generating the patient letter.</p></div></li>
        </ol>
        <div className={styles.footer}><p><strong>AI transcription is in beta.</strong> Always review the generated note for accuracy.</p><button type="button" className={styles.doneButton} onClick={close}>Got it</button></div>
      </Dialog>
    </>
  );
}
